import classNames from "classnames/bind";
import { AnimatePresence, motion, type HTMLMotionProps } from "framer-motion";
import React, {
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import styles from "./SequentialContentReveal.module.scss";

type SequentialContentRevealProps = PropsWithChildren<
  {
    delay?: number;
  } & Omit<HTMLMotionProps<"div">, "initial" | "animate" | "exit">
>;

const cx = classNames.bind(styles);

export const SequentialContentReveal = ({
  children,
  delay = 500,
  ...restProps
}: SequentialContentRevealProps) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const actualContent = useMemo(() => {
    return toReactNodeArray(children);
  }, [children]);

  useEffect(() => {
    if (visibleCount < actualContent.length) {
      const timer = setTimeout(() => {
        setVisibleCount((prev) => prev + 1);
      }, delay);

      return () => clearTimeout(timer);
    } else return;
  }, [visibleCount, actualContent.length, delay]);

  return (
    <>
      <AnimatePresence>
        {actualContent.slice(0, visibleCount).map((item, index) => (
          <motion.div
            key={index}
            className={cx("animate", restProps.className)}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {
                opacity: 0,
                position: "absolute",
                y: "10px",
                filter: "blur(2px)",
              },
              visible: {
                opacity: 1,
                y: "0px",
                position: "static",
                filter: "blur(0px)",
              },
            }}
            {...restProps}
          >
            {item}
          </motion.div>
        ))}
      </AnimatePresence>
    </>
  );
};

export const DelayedContentReveal = ({
  children,
  delay,
  hiddenRenderBeforeDelay = false,
}: PropsWithChildren<{ delay: number; hiddenRenderBeforeDelay?: boolean }>) => {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setDone(true), delay);

    return () => clearTimeout(showTimer);
  }, []);

  return done ? (
    <>{children}</>
  ) : hiddenRenderBeforeDelay ? (
    <div style={{ display: "none" }}>{children}</div>
  ) : null;
};

function toReactNodeArray(node: ReactNode): ReactNode[] {
  const children = React.Children.toArray(node);

  return children.flatMap((child) => {
    if (
      isValidElement(child) &&
      child.type === React.Fragment &&
      child.props?.children
    ) {
      // Recursively unwrap nested fragments
      return toReactNodeArray(child.props.children);
    }
    return [child];
  });
}
